import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { FadeUp } from "./Reveal";

const SYSTEMS = [
        { name: "NMI", full: "Natant Meta Instruct", verb: "Designs", desc: "Turns intent into every floor." },
        { name: "Allostasis", full: "Continuous tuning", verb: "Tunes", desc: "Holds every journey to its KPI." },
        { name: "Nibras", full: "The memory layer", verb: "Remembers", desc: "One shared memory on every floor." },
];

// A deeper canvas: intent housed in the core, flowing down through the working
// layers of the company and out to the five floors it replaces. Abstracted on
// purpose — the full architecture stays under the hood.
const LAYERS = [
        "Surfaces — where work happens",
        "Journeys — each bound to its KPI",
        "Agents — acting inside and out",
        "Memory — one shared truth",
];

const FLOORS = [
        { label: "CRM", x: 40, glyph: "person" },
        { label: "ERP", x: 190, glyph: "bars" },
        { label: "eCom", x: 340, glyph: "cart" },
        { label: "OMS", x: 490, glyph: "box" },
        { label: "Desk", x: 640, glyph: "headset" },
];

const CORE = { x: 360, y: 58 }; // pill: x 252..468, y 24..92
const LAYER = { x: 110, w: 500, h: 44, gap: 14, y0: 128 };
const TILE = { y: 396, w: 64, h: 84 };
const RAILS = [598, 618, 638];

const layerY = (i) => LAYER.y0 + i * (LAYER.h + LAYER.gap); // 128, 186, 244, 302
const FAN_FROM = layerY(3) + LAYER.h; // 346

function FloorGlyph({ kind, cx, cy }) {
        const p = { fill: "none", stroke: "#6b7078", strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round" };
        switch (kind) {
                case "person":
                        return (
                                <g {...p}>
                                        <circle cx={cx} cy={cy - 6} r="4" />
                                        <path d={`M${cx - 8},${cy + 8} q8,-12 16,0`} />
                                </g>
                        );
                case "bars":
                        return (
                                <g {...p}>
                                        <line x1={cx - 7} y1={cy + 8} x2={cx - 7} y2={cy - 2} />
                                        <line x1={cx} y1={cy + 8} x2={cx} y2={cy - 9} />
                                        <line x1={cx + 7} y1={cy + 8} x2={cx + 7} y2={cy + 2} />
                                </g>
                        );
                case "cart":
                        return (
                                <g {...p}>
                                        <path d={`M${cx - 9},${cy - 5} h3 l2.5,10 h11 l2.5,-8 h-12`} />
                                        <circle cx={cx - 3} cy={cy + 9} r="1.7" />
                                        <circle cx={cx + 6} cy={cy + 9} r="1.7" />
                                </g>
                        );
                case "box":
                        return (
                                <g {...p}>
                                        <rect x={cx - 8} y={cy - 6} width="16" height="14" rx="1.5" />
                                        <path d={`M${cx - 8},${cy - 1} h16 M${cx},${cy - 6} v5`} />
                                </g>
                        );
                case "headset":
                        return (
                                <g {...p}>
                                        <path d={`M${cx - 8},${cy + 3} a8,8 0 0 1 16,0`} />
                                        <rect x={cx - 10} y={cy + 1} width="3.4" height="7" rx="1.6" />
                                        <rect x={cx + 6.6} y={cy + 1} width="3.4" height="7" rx="1.6" />
                                        <path d={`M${cx + 8.3},${cy + 8} q0,3.5 -4.3,3.5`} />
                                </g>
                        );
                default:
                        return null;
        }
}

export default function Stack() {
        const ref = useRef(null);
        // Scroll-driven build: the blocked marker shakes loose, becomes the core
        // housing intent, then the working layers and floors connect top to bottom.
        const { scrollYProgress } = useScroll({ target: ref, offset: ["start 95%", "start 25%"] });
        const xOpacity = useTransform(scrollYProgress, [0.1, 0.26], [1, 0]);
        const nOpacity = useTransform(scrollYProgress, [0.16, 0.34], [0, 1]);
        const nScale = useTransform(scrollYProgress, [0.16, 0.38], [0.6, 1]);
        const trunkLen = useTransform(scrollYProgress, [0.3, 0.42], [0, 1]);
        const l1 = useTransform(scrollYProgress, [0.4, 0.52], [0, 1]);
        const l2 = useTransform(scrollYProgress, [0.5, 0.62], [0, 1]);
        const l3 = useTransform(scrollYProgress, [0.6, 0.72], [0, 1]);
        const l4 = useTransform(scrollYProgress, [0.7, 0.82], [0, 1]);
        const fanLen = useTransform(scrollYProgress, [0.78, 0.9], [0, 1]);
        const floorsOpacity = useTransform(scrollYProgress, [0.84, 0.96], [0, 1]);
        const floorsY = useTransform(scrollYProgress, [0.84, 0.96], [14, 0]);
        const pulsesOpacity = useTransform(scrollYProgress, [0.94, 1], [0, 1]);
        const layerStyles = [l1, l2, l3, l4].map((o) => ({ opacity: o }));

        return (
                <section id="stack" data-testid="stack-section" className="relative pt-10 pb-12 md:pt-12 md:pb-16 border-t border-[var(--border-line)]">
                        <div className="max-container">
                                <FadeUp>
                                        <div className="step-eyebrow mb-5">Natant</div>
                                        <h2 className="h2x text-3xl md:text-5xl max-w-[24ch] mb-6 md:mb-8">
                                                One AI native <span className="acc">Business OS</span> connecting every business
                                                floor of the organisation.
                                        </h2>
                                </FadeUp>

                                {/* the answer, drawn — intent housed in the core, flowing down to every floor */}
                                <FadeUp delay={0.08}>
                                        <div ref={ref} className="border border-[var(--border-line)] rounded-2xl bg-[var(--surface)] p-4 md:p-8 mb-6">
                                                <svg viewBox="0 0 720 516" className="w-full h-auto max-w-[880px] mx-auto" role="img" aria-label="The Natant core houses your intent and connects the working layers of the company down to CRM, ERP, eCom, OMS and Desk">
                                                        <style>{`
                                                                .nx-shake { transform-box: fill-box; transform-origin: center; animation: nx-shake 1.8s ease-in-out infinite; }
                                                                @keyframes nx-shake {
                                                                        0%, 100% { transform: translate(0, 0) rotate(0deg); }
                                                                        15% { transform: translate(-1.6px, 0.6px) rotate(-5deg); }
                                                                        35% { transform: translate(1.6px, -0.6px) rotate(4deg); }
                                                                        55% { transform: translate(-1.1px, 0) rotate(-3deg); }
                                                                        75% { transform: translate(1.1px, 0.6px) rotate(2deg); }
                                                                }
                                                                @media (prefers-reduced-motion: reduce) { .nx-shake { animation: none !important; } }
                                                        `}</style>

                                                        {/* the blocked marker — shaking loose */}
                                                        <motion.g className="nx-shake" style={{ opacity: xOpacity }}>
                                                                <circle cx={CORE.x} cy={CORE.y} r="17" fill="#8b3dff" />
                                                                <path d={`M${CORE.x - 7},${CORE.y - 7} L${CORE.x + 7},${CORE.y + 7} M${CORE.x + 7},${CORE.y - 7} L${CORE.x - 7},${CORE.y + 7}`} stroke="#ffffff" strokeWidth="2.4" strokeLinecap="round" />
                                                        </motion.g>

                                                        {/* the core it becomes — with your intent housed inside */}
                                                        <motion.g style={{ opacity: nOpacity, scale: nScale, transformBox: "fill-box", transformOrigin: "center" }}>
                                                                <rect x={CORE.x - 108} y={CORE.y - 34} width="216" height="68" rx="34" fill="rgba(139,61,255,0.06)" stroke="#8b3dff" strokeOpacity="0.55" strokeWidth="1.4" />
                                                                <circle cx={CORE.x - 74} cy={CORE.y} r="17" fill="#8b3dff" />
                                                                <text x={CORE.x - 74} y={CORE.y + 6} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="800" fontSize="15" fill="#ffffff" textAnchor="middle">N</text>
                                                                <text x={CORE.x - 46} y={CORE.y - 4} fontFamily="'JetBrains Mono', monospace" fontSize="9.5" letterSpacing="2.4" fill="#8b3dff">YOUR INTENT</text>
                                                                <text x={CORE.x - 46} y={CORE.y + 13} fontFamily="'IBM Plex Sans', sans-serif" fontSize="11.5" fontWeight="600" fill="#1a1a1a">housed in the Natant OS</text>
                                                        </motion.g>

                                                        {/* trunk from the core into the working layers */}
                                                        <motion.line
                                                                x1={CORE.x} y1={CORE.y + 34} x2={CORE.x} y2={LAYER.y0}
                                                                stroke="#8b3dff" strokeWidth="1.4" strokeOpacity="0.7"
                                                                style={{ pathLength: trunkLen }}
                                                        />

                                                        {/* the working layers — abstracted, one per capability */}
                                                        {LAYERS.map((label, i) => {
                                                                const y = layerY(i);
                                                                return (
                                                                        <motion.g key={label} style={layerStyles[i]}>
                                                                                {i > 0 && (
                                                                                        <line x1={CORE.x} y1={y - LAYER.gap} x2={CORE.x} y2={y} stroke="#8b3dff" strokeWidth="1.2" strokeOpacity="0.5" />
                                                                                )}
                                                                                <rect x={LAYER.x} y={y} width={LAYER.w} height={LAYER.h} rx="12" fill="#ffffff" stroke="#1a1a1a" strokeOpacity="0.18" strokeWidth="1.2" />
                                                                                <circle cx={LAYER.x + 22} cy={y + LAYER.h / 2} r="4" fill="#8b3dff" />
                                                                                <text x={LAYER.x + 40} y={y + LAYER.h / 2 + 3.5} fontFamily="'JetBrains Mono', monospace" fontSize="10" letterSpacing="1.6" fill="#1a1a1a">{label}</text>
                                                                        </motion.g>
                                                                );
                                                        })}

                                                        {/* three quiet rails — designs, tunes, remembers — running through every layer */}
                                                        <motion.g style={layerStyles[3]}>
                                                                {RAILS.map((x) => (
                                                                        <g key={x}>
                                                                                <line x1={x} y1={LAYER.y0 + 6} x2={x} y2={FAN_FROM - 6} stroke="#8b3dff" strokeWidth="1.1" strokeOpacity="0.4" strokeDasharray="2 6" />
                                                                                <path d={`M${x - 3.5},${LAYER.y0 + 12} L${x},${LAYER.y0 + 5} L${x + 3.5},${LAYER.y0 + 12}`} fill="none" stroke="#8b3dff" strokeWidth="1.3" strokeOpacity="0.55" strokeLinecap="round" />
                                                                        </g>
                                                                ))}
                                                        </motion.g>

                                                        {/* from the layers out to the five floors */}
                                                        <g stroke="#8b3dff" strokeWidth="1.4" strokeOpacity="0.6" fill="none">
                                                                {FLOORS.map((f) => (
                                                                        <motion.line
                                                                                key={f.label}
                                                                                x1={CORE.x}
                                                                                y1={FAN_FROM}
                                                                                x2={f.x + TILE.w / 2}
                                                                                y2={TILE.y}
                                                                                style={{ pathLength: fanLen }}
                                                                        />
                                                                ))}
                                                        </g>

                                                        {/* live pulses — down the trunk, out to every floor */}
                                                        <motion.g style={{ opacity: pulsesOpacity }}>
                                                                <circle r="3.2" fill="#8b3dff">
                                                                        <animateMotion dur="1.8s" begin="0s" repeatCount="indefinite" path={`M${CORE.x},${CORE.y + 34} L${CORE.x},${FAN_FROM}`} />
                                                                </circle>
                                                                {FLOORS.map((f, i) => (
                                                                        <circle key={f.label} r="3.2" fill="#8b3dff">
                                                                                <animateMotion
                                                                                        dur="2.4s"
                                                                                        begin={`${i * 0.45}s`}
                                                                                        repeatCount="indefinite"
                                                                                        path={`M${CORE.x},${FAN_FROM} L${f.x + TILE.w / 2},${TILE.y}`}
                                                                                />
                                                                        </circle>
                                                                ))}
                                                        </motion.g>

                                                        {/* the five floors — named, alive, connected */}
                                                        <motion.g style={{ opacity: floorsOpacity, y: floorsY }}>
                                                                {FLOORS.map((f) => (
                                                                        <g key={f.label}>
                                                                                <rect x={f.x} y={TILE.y} width={TILE.w} height={TILE.h} rx="14" fill="#ffffff" stroke="#1a1a1a" strokeOpacity="0.25" strokeWidth="1.2" />
                                                                                <FloorGlyph kind={f.glyph} cx={f.x + TILE.w / 2} cy={TILE.y + 34} />
                                                                                <line x1={f.x + 16} y1={TILE.y + 58} x2={f.x + TILE.w - 16} y2={TILE.y + 58} stroke="#1a1a1a" strokeOpacity="0.14" strokeWidth="1.4" />
                                                                                <circle cx={f.x + TILE.w / 2} cy={TILE.y + 70} r="4" fill="#8b3dff" />
                                                                                <text x={f.x + TILE.w / 2} y={TILE.y + TILE.h + 24} fontFamily="'JetBrains Mono', monospace" fontSize="10" letterSpacing="1.8" fill="#6b7078" textAnchor="middle">{f.label}</text>
                                                                        </g>
                                                                ))}
                                                        </motion.g>
                                                </svg>
                                        </div>
                                </FadeUp>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {SYSTEMS.map((s, i) => (
                                                <FadeUp key={s.name} delay={0.1 + i * 0.08}>
                                                        <div
                                                                data-testid={`stack-system-${s.name.toLowerCase()}`}
                                                                className="border border-[var(--border-line)] rounded-2xl p-6 h-full bg-[var(--surface)]"
                                                                style={{ background: "rgba(139,61,255,0.06)" }}
                                                        >
                                                                <div className="flex items-baseline gap-2 mb-1">
                                                                        <span className="font-display font-bold text-lg tracking-tight">{s.name}</span>
                                                                        <span className="font-mono text-[10px] tracking-[0.22em] uppercase acc">{s.verb}</span>
                                                                </div>
                                                                <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-[var(--bone-dim)] mb-2">{s.full}</div>
                                                                <p className="text-sm text-[var(--bone-dim)] leading-relaxed">{s.desc}</p>
                                                        </div>
                                                </FadeUp>
                                        ))}
                                </div>
                        </div>
                </section>
        );
}
